import { useMemo } from 'react'

import { Layout, Text } from '@loadsmart/loadsmart-ui'

import { Icon } from 'core/ui/components/icon/Icon'

import {
  groupBySort,
  isSubmitted,
  type BulkRate,
  type GroupedByDeadlinesBulkRate,
  type GroupedByLanesBulkRate,
} from '../../domain/BulkRate'
import { isQuoteExpired } from '../../domain/Quote'
import { type SortBy } from '../../domain/SortBy'
import { formatSpotQuoteDeadline } from '../../presenter/spotQuotePresenter'
import { SpotQuoteDetails } from '../SpotQuoteDetails'

function GroupHeader({ group }: { group: GroupedByLanesBulkRate | GroupedByDeadlinesBulkRate }) {
  if ('deadline' in group) {
    return <Text variant="body-bold">{formatSpotQuoteDeadline(group.deadline)}</Text>
  }

  return (
    <Layout.Group align="center" space="s">
      <Text variant="body-bold">{group.pickup}</Text>
      <Icon name="arrow-right" />
      <Text variant="body-bold">{group.delivery}</Text>
    </Layout.Group>
  )
}

export type BulkSpotQuoteRatesProps = {
  sortBy?: SortBy
  rates: Array<BulkRate>
  disabled?: boolean
  onNotesChange?: (rateId: string, shipperNotes: string) => void
  onRateChange?: (rateId: string, flatRate: number) => void
}

export function BulkSpotQuoteRates({
  sortBy = 'lane',
  rates,
  disabled = false,
  onRateChange,
  onNotesChange,
}: BulkSpotQuoteRatesProps) {
  const groupedRates = useMemo(() => groupBySort(rates, sortBy), [sortBy, rates])

  return (
    <Layout.Stack>
      {Object.keys(groupedRates).map(key => {
        const group = groupedRates[key]

        return (
          <Layout.Stack space="m" key={key}>
            <GroupHeader group={group} />
            {group.rates.map(rate => {
              return (
                <SpotQuoteDetails
                  key={rate.uuid}
                  id={rate.uuid}
                  spotQuote={rate}
                  isSubmitted={isSubmitted(rate)}
                  disabled={disabled || isQuoteExpired(rate.quote)}
                  notes={rate.carrierNotes}
                  quoteType="bulk"
                  onNotesChange={shipperNotes => onNotesChange?.(rate.uuid, shipperNotes)}
                  onRateChange={flatRate => onRateChange?.(rate.uuid, flatRate)}
                />
              )
            })}
          </Layout.Stack>
        )
      })}
    </Layout.Stack>
  )
}
